/**
 * QuickSearch - Command palette style search (Cmd+K)
 * 
 * Filters projects and teams by name. Keyboard navigable.
 */

import React, { useEffect, useRef, useState } from 'react';
import type { Project } from '../../stores/projectStore';

interface QuickSearchProps {
  query: string;
  onQueryChange: (query: string) => void;
  onClose: () => void;
  onSelectTeam: (teamId: number) => void;
  projects: Project[];
}

export const QuickSearch: React.FC<QuickSearchProps> = ({
  query,
  onQueryChange,
  onClose,
  onSelectTeam,
  projects,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Collect all teams with project context
  const allTeams: Array<{ id: number; name: string; projectName: string }> = [];
  for (const project of projects) {
    const teams = (project as any).teams || [];
    for (const team of teams) {
      allTeams.push({
        id: team.id,
        name: team.name,
        projectName: project.name,
      });
    }
  }

  // Filter by query
  const filtered = query.trim()
    ? allTeams.filter(t =>
        t.name.toLowerCase().includes(query.toLowerCase()) ||
        t.projectName.toLowerCase().includes(query.toLowerCase())
      )
    : allTeams;

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      onSelectTeam(filtered[selectedIndex].id);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/50" onClick={onClose}>
      <div
        className="w-96 max-h-96 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="px-4 py-3 border-b border-gray-700">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search teams..."
            className="w-full bg-transparent text-white text-sm outline-none placeholder-gray-500"
          />
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-gray-500 text-sm">
              {query ? 'No teams found' : 'No teams available'}
            </div>
          ) : (
            filtered.map((team, idx) => (
              <button
                key={team.id}
                className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors ${
                  idx === selectedIndex
                    ? 'bg-indigo-600/20 text-white'
                    : 'text-gray-300 hover:bg-gray-700/50'
                }`}
                onClick={() => onSelectTeam(team.id)}
                onMouseEnter={() => setSelectedIndex(idx)}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{team.name}</div>
                  <div className="text-[10px] text-gray-500">{team.projectName}</div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-gray-700 text-gray-600 text-[10px] flex gap-4">
          <span>Up/Down to navigate</span>
          <span>Enter to select</span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
};
